import { ReactNode, memo, useCallback, useState } from 'react';

import { Menu } from '@headlessui/react';
import { AnnouncementIcon, CheckCircleFilled, ExclamationRoundedFilled, OfferIcon } from 'icons';
import { FormattedMessage } from 'localization';
import { twMerge } from 'tailwind-merge';
import { match } from 'ts-pattern';

import Button, { ButtonVariant } from '../Button';
import Copy from '../Copy';

export enum NotificationTheme {
  Primary = 'primary',
  Danger = 'danger',
  Info = 'info',
  Success = 'success',
  Warning = 'warning',
}

export enum NotificationIcon {
  Info = 'info',
  Cancelling = 'cancelling',
  Offer = 'offer',
  Announcement = 'announcement',
  Success = 'success',
  Warning = 'warning',
}

export type NotificationProps = {
  theme?: NotificationTheme;
  icon: NotificationIcon;
  title: ReactNode;
  description: ReactNode;
  coloredBackground?: boolean;
  actionButtonTitle?: string;
  onClickAction?: () => void;
  onAsyncClickAction?: () => Promise<void>;
  canBeClosed?: boolean;
};

const Notification = ({
  theme = NotificationTheme.Primary,
  icon,
  coloredBackground,
  title,
  description,
  actionButtonTitle,
  onClickAction,
  onAsyncClickAction,
  canBeClosed,
}: NotificationProps) => {
  const [isLoading, setIsLoading] = useState(false);

  const handleAsyncClick = useCallback(async () => {
    if (!onAsyncClickAction) return;
    setIsLoading(true);
    try {
      await onAsyncClickAction();
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [onAsyncClickAction]);

  const iconToUse = match(icon)
    .with(NotificationIcon.Info, () => <ExclamationRoundedFilled width="20" height="20" />)
    .with(NotificationIcon.Cancelling, () => (
      <ExclamationRoundedFilled width="20" height="20" className="rotate-180" />
    ))
    .with(NotificationIcon.Offer, () => <OfferIcon width="22" height="22" />)
    .with(NotificationIcon.Announcement, () => <AnnouncementIcon width="20" height="20" />)
    .with(NotificationIcon.Success, () => <CheckCircleFilled width="18" height="18" />)
    .with(NotificationIcon.Warning, () => <ExclamationRoundedFilled width="20" height="20" />)
    .exhaustive();

  const buttonTheme = coloredBackground
    ? match(theme)
        .with(NotificationTheme.Primary, () => ButtonVariant.violet)
        .with(NotificationTheme.Info, () => ButtonVariant.blue)
        .with(NotificationTheme.Success, () => ButtonVariant.green)
        .with(NotificationTheme.Danger, () => ButtonVariant.Danger)
        .with(NotificationTheme.Warning, () => ButtonVariant.orange)
        .exhaustive()
    : ButtonVariant.Secondary;

  return (
    <div
      className={twMerge(
        'rounded-[10px] p-4',
        theme === 'primary' && coloredBackground && 'bg-v2violet/[.08] text-v2violet',
        theme === 'info' && coloredBackground && 'bg-v2blue-600/[.08] text-v2blue-600',
        theme === 'success' && coloredBackground && 'bg-v2green/[.08] text-v2green',
        theme === 'danger' && coloredBackground && 'bg-v2red/[.08] text-v2red',
        theme === 'warning' && coloredBackground && 'bg-v2orange/[.08] text-v2orange',
      )}
    >
      <div className="flex gap-3">
        <div
          className={twMerge(
            'flex items-center justify-center w-[32px] h-[32px] min-w-[32px] min-h-[32px] rounded-[10px]',
            theme === 'primary' && 'bg-v2violet/20 text-v2violet',
            theme === 'info' && 'bg-v2blue-600/20 text-v2blue-600',
            theme === 'success' && 'bg-v2green/20 text-v2green',
            theme === 'danger' && 'bg-v2red/20 text-v2red ',
            theme === 'warning' && 'bg-v2orange/20 text-v2orange',
          )}
        >
          {iconToUse}
        </div>

        <div className="flex flex-col text-left">
          <Copy
            noColor
            type="copy2"
            weight="semibold"
            as="div"
            className={!coloredBackground ? 'text-v2blueGray-800' : ''}
          >
            {title}
          </Copy>
          <Copy
            noColor
            type="copy2"
            weight="regular"
            as="div"
            className={twMerge(
              'mt-1 whitespace-pre-wrap',
              !coloredBackground ? 'text-v2blueGray-600' : 'opacity-70',
            )}
          >
            {description}
          </Copy>

          {actionButtonTitle && (
            <Menu.Item>
              {({ close }) => (
                <div className="flex">
                  <Button
                    loading={isLoading}
                    className="mt-4"
                    theme={buttonTheme}
                    onClick={async (e) => {
                      if (onClickAction) {
                        onClickAction();
                      } else {
                        e.preventDefault(); // ❌ stop auto-close
                        await handleAsyncClick();
                      }
                      close(); //  manually close after async
                    }}
                  >
                    {actionButtonTitle}
                  </Button>
                </div>
              )}
            </Menu.Item>
          )}

          {canBeClosed && (
            <div className="flex">
              <Button
                loading={isLoading}
                className="mt-4"
                theme={buttonTheme}
                onClick={async (e) => {
                  if (onClickAction) {
                    onClickAction();
                  } else {
                    e.preventDefault(); // ❌ stop auto-close
                    await handleAsyncClick();
                  }
                  close(); //  manually close after async
                }}
              >
                <FormattedMessage id="general.close" />
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo<NotificationProps>(Notification);
